import { createSlice } from '@reduxjs/toolkit';
import { PayloadAction } from '@reduxjs/toolkit';
import { initialState } from './initialState';
import { ApiExError } from '@shared';

function initializeStateIfNeeded(state: IllustrationState) {
  // Initialize fields if they don't exist (for backwards compatibility)
  if (!state.activeRequests) {
    state.activeRequests = {};
  }
  if (!state.completedIllustrations) {
    state.completedIllustrations = {};
  }
  if (!state.erroredRequests) {
    state.erroredRequests = {};
  }
  if (!state.errors) {
    state.errors = [];
  }
  if (state.hasActiveRequest === undefined) {
    state.hasActiveRequest = false;
  }
  if (!state.shownErrorToasts) {
    state.shownErrorToasts = {};
  }
}

function updateGlobalActiveState(state: IllustrationState) {
  state.hasActiveRequest = Object.keys(state.activeRequests).length > 0;
}

const MAX_POLL_COUNT = 30;

const illustrationSlice = createSlice({
  name: 'illustration',
  initialState: initialState.illustration,
  reducers: {
    postIllustrationRequestRequest(state, action: PayloadAction<IllustrationRequestPayload>) {
      initializeStateIfNeeded(state);
      const { insuranceQuoteId } = action.payload;

      // Clear any previous error for this quote
      delete state.erroredRequests[insuranceQuoteId];

      state.activeRequests[insuranceQuoteId] = {
        requestId: '',
        insuranceQuoteId,
        status: 'initiated',
        currentPollCount: 0,
        pdfUrl: null,
        downloaded: false,
        errors: [],
        premiumAmount: action.payload.premiumAmount,
      };
      updateGlobalActiveState(state);
    },
    postIllustrationRequestFailure(
      state,
      action: PayloadAction<ApiExError & { insuranceQuoteId: string }>,
    ) {
      initializeStateIfNeeded(state);
      const { insuranceQuoteId } = action.payload;
      const activeRequest = state.activeRequests[insuranceQuoteId];

      if (activeRequest) {
        activeRequest.status = 'error';
        activeRequest.errors.push('Error creating illustration request');

        // Store error information before deleting active request
        state.erroredRequests[insuranceQuoteId] = {
          errors: [...activeRequest.errors],
          errorDateTime: new Date(),
        };

        delete state.activeRequests[insuranceQuoteId];
        updateGlobalActiveState(state);
      }
    },
    postIllustrationRequestResponse(
      state,
      action: PayloadAction<{ requestId: string; insuranceQuoteId: string }>,
    ) {
      initializeStateIfNeeded(state);
      const { requestId, insuranceQuoteId } = action.payload;

      if (state.activeRequests[insuranceQuoteId]) {
        state.activeRequests[insuranceQuoteId].requestId = requestId;
        state.activeRequests[insuranceQuoteId].status = 'polling';
      }
    },
    fetchIllustrationStatusByRequestIdRequest(
      state,
      action: PayloadAction<IllustrationRequestIdProp>,
    ) {
      initializeStateIfNeeded(state);
      Object.values(state.activeRequests).forEach(request => {
        if (request.requestId === action.payload.requestId) {
          request.status = 'polling';
        }
      });
    },
    fetchIllustrationStatusByRequestIdFailure(
      state,
      action: PayloadAction<ApiExError & { requestId: string }>,
    ) {
      initializeStateIfNeeded(state);
      const { requestId } = action.payload;
      const activeRequest = Object.values(state.activeRequests).find(
        req => req.requestId === requestId,
      );

      if (activeRequest) {
        activeRequest.status = 'error';
        activeRequest.errors.push('Error retrieving illustration status');
      }
    },
    fetchIllustrationStatusByRequestIdResponse(
      state,
      action: PayloadAction<IllustrationRequestDbRecord>,
    ) {
      initializeStateIfNeeded(state);
      const record = action.payload;
      const activeRequest = Object.values(state.activeRequests).find(
        req => req.requestId === record.requestId,
      );

      if (activeRequest) {
        activeRequest.currentPollCount += 1;

        switch (record.status) {
          case 'completed':
            activeRequest.status = 'completed';
            activeRequest.pdfUrl = record.pdfUrl || null;
            activeRequest.downloaded = record.downloaded || false;

            if (record.pdfUrl) {
              state.completedIllustrations[activeRequest.insuranceQuoteId] = {
                requestId: activeRequest.requestId,
                insuranceQuoteId: activeRequest.insuranceQuoteId,
                pdfUrl: record.pdfUrl,
                downloaded: record.downloaded || false,
                completedAt: new Date(),
                premiumAmount: activeRequest.premiumAmount,
                consentCompleted: false, // Track if user completed consent step
                responseData: record.responseData,
                fundingSourceId: record.fundingSourceId,
              };
            }

            delete state.activeRequests[activeRequest.insuranceQuoteId];
            updateGlobalActiveState(state);
            break;

          case 'error':
            activeRequest.status = 'error';
            activeRequest.errors.push(record.error || 'Illustration generation failed');

            // Store error information before deleting active request
            state.erroredRequests[activeRequest.insuranceQuoteId] = {
              errors: [...activeRequest.errors],
              errorDateTime: new Date(),
            };

            delete state.activeRequests[activeRequest.insuranceQuoteId];
            updateGlobalActiveState(state);
            break;

          default:
            activeRequest.status = 'polling';
        }

        if (activeRequest.currentPollCount > MAX_POLL_COUNT) {
          activeRequest.status = 'error';
          activeRequest.errors.push('Request timed out.');

          // Store error information before deleting active request
          state.erroredRequests[activeRequest.insuranceQuoteId] = {
            errors: [...activeRequest.errors],
            errorDateTime: new Date(),
          };

          delete state.activeRequests[activeRequest.insuranceQuoteId];
          updateGlobalActiveState(state);
        }
      }
    },
    illustrationPollingTimeout(state, action: PayloadAction<{ insuranceQuoteId: string }>) {
      initializeStateIfNeeded(state);
      const { insuranceQuoteId } = action.payload;
      const activeRequest = state.activeRequests[insuranceQuoteId];

      if (activeRequest) {
        activeRequest.status = 'error';
        activeRequest.errors.push('Illustration request timed out. Please try again.');

        // Store error information before deleting active request
        state.erroredRequests[insuranceQuoteId] = {
          errors: [...activeRequest.errors],
          errorDateTime: new Date(),
        };

        delete state.activeRequests[insuranceQuoteId];
        updateGlobalActiveState(state);
      }
    },
    markIllustrationAsDownloaded(state, action: PayloadAction<IllustrationRequestIdProp>) {
      initializeStateIfNeeded(state);
      const { requestId } = action.payload;

      Object.values(state.activeRequests).forEach(request => {
        if (request.requestId === requestId) {
          request.downloaded = true;
        }
      });

      Object.values(state.completedIllustrations).forEach(illustration => {
        if (illustration.requestId === requestId) {
          illustration.downloaded = true;
        }
      });
    },
    addIllustrationErrorMessage(state, action: PayloadAction<string>) {
      initializeStateIfNeeded(state);
      state.errors.push(action.payload);
    },
    clearIllustrationRequest(state, action: PayloadAction<{ insuranceQuoteId: string }>) {
      initializeStateIfNeeded(state);
      const { insuranceQuoteId } = action.payload;
      delete state.activeRequests[insuranceQuoteId];
      updateGlobalActiveState(state);
    },
    markErrorToastAsShown(state, action: PayloadAction<string>) {
      initializeStateIfNeeded(state);
      state.shownErrorToasts[action.payload] = true;
    },
    clearShownErrorToasts(state) {
      initializeStateIfNeeded(state);
      state.shownErrorToasts = {};
    },
    markIllustrationConsentCompleted(state, action: PayloadAction<{ insuranceQuoteId: string }>) {
      initializeStateIfNeeded(state);
      const { insuranceQuoteId } = action.payload;

      if (state.completedIllustrations[insuranceQuoteId]) {
        state.completedIllustrations[insuranceQuoteId].consentCompleted = true;
      }
    },
    clearStuckIllustrationRequests(state) {
      initializeStateIfNeeded(state);
      const now = new Date();

      Object.keys(state.activeRequests).forEach(quoteId => {
        const request = state.activeRequests[quoteId];
        // Check if request has been active for more than 5 minutes without a valid requestId or in polling state too long
        if (request && (!request.requestId || request.currentPollCount > 60)) {
          // 60 polls = 5 minutes
          // Move to errored requests
          state.erroredRequests[quoteId] = {
            errors: ['Request timed out or became stuck'],
            errorDateTime: now,
          };
          delete state.activeRequests[quoteId];
        }
      });
      updateGlobalActiveState(state);
    },
    resetIllustrationState(state) {
      state.activeRequests = {};
      state.completedIllustrations = {};
      state.erroredRequests = {};
      state.errors = [];
      state.hasActiveRequest = false;
      state.shownErrorToasts = {};
    },
  },
});

export const {
  postIllustrationRequestRequest,
  postIllustrationRequestFailure,
  postIllustrationRequestResponse,
  fetchIllustrationStatusByRequestIdRequest,
  fetchIllustrationStatusByRequestIdFailure,
  fetchIllustrationStatusByRequestIdResponse,
  illustrationPollingTimeout,
  markIllustrationAsDownloaded,
  addIllustrationErrorMessage,
  clearIllustrationRequest,
  markErrorToastAsShown,
  clearShownErrorToasts,
  markIllustrationConsentCompleted,
  resetIllustrationState,
} = illustrationSlice.actions;

export const illustrationSliceReducer = illustrationSlice.reducer;
